import { Button } from "$components/button/button";
import { CopyIcon } from "$components/icons";
import { ImagingStudy } from "$global-providers/rxdb-provider/collections/imaging-study";
import { toast } from "$global-providers/toast-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { RxCollection } from "rxdb";

const copyToClipboard = async (text: string) => {
  // Modern browsers with Clipboard API support
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback for older browsers
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  textArea.remove();
};

const createImagingStudy = async (
  accessionNumber: string,
  patientId: string,
  userId: string,
  imagingStudyCollection: RxCollection<ImagingStudy>,
) => {
  if (!imagingStudyCollection) {
    return;
  }

  const imagingStudyInput = {
    id: randomUniqueId(),
    accessionNumber,
    elephantPatientId: patientId,
    meta: {
      createdAt: new Date().toISOString(),
      createdByUserId: userId,
      updatedAt: new Date().toISOString(),
      updatedByUserId: userId,
    },
  };

  await imagingStudyCollection.upsert(imagingStudyInput);
};

export const GenerateImagingStudyCopyIdButton: React.FC<{
  patientId: string;
  userId: string;
  imagingStudyCollection: RxCollection<ImagingStudy>;
}> = ({ patientId, userId, imagingStudyCollection }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      // Generate the accession number immediately within user interaction context
      const accessionNumber = randomUniqueId(16);

      // Copy to clipboard immediately while still in user interaction context
      await copyToClipboard(accessionNumber);
      toast.success("POCUS ID copied to clipboard");

      // Then create the imaging study asynchronously
      await createImagingStudy(
        accessionNumber,
        patientId,
        userId,
        imagingStudyCollection,
      );
    } catch (error) {
      toast.error("Failed to copy to clipboard");
      Sentry.captureException(error);
    } finally {
      // Add a small delay to prevent rapid successive clicks
      setTimeout(() => setIsProcessing(false), 1000);
    }
  };

  return (
    <Button
      variant="contained"
      color="secondary"
      startIcon={<CopyIcon />}
      onClick={handleClick}
      disabled={isProcessing}
    >
      Copy POCUS ID
    </Button>
  );
};
